import React, { useState, useEffect, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../shared/components/ui/Button';
import deleteIcon from '../../shared/assets/svg/cancel_icon.svg';
import Multiselect from 'multiselect-react-dropdown';
import toast from 'react-hot-toast';
import {
  getAllBlocks,
  getAllBuilderCategories,
  getAllWorkTypes,
  getContractorAndArchitect,
  getPlcbyBlockId,
  getAllItemsByProjectAndCategoryID
} from '../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import { getUsers } from '../../super-admin/services/api/implementation/impl';
import Cookies from 'js-cookie';
import FormInput from '../../shared/components/ui/FormInput';
import mixpanel from 'mixpanel-browser';
import { capitalizeWords } from '../utils/stringUtils';

const validationSchema = yup.object().shape({
  task_name: yup.string().required('Task name is required'),
  idleTime: yup.string().required('Idle time is required'),
  duration: yup.string().required('Duration is required'),
  task_ids: yup.array().when('$taskPositionType', {
    is: (val) => val === 'Other',
    then: (schema) => schema.min(1, 'Dependency task required'),
    otherwise: (schema) => schema
  }),
  drawingRequired: yup.string().required('Select if drawing is required'),
  material_budget: yup.string().nullable(),
  labour_budget: yup.string().nullable(),
  sp_ids: yup.array().nullable(),
  architect_ids: yup.array().nullable(),
  engineer_ids: yup.array().min(1, 'At least one employee must be selected'),
  unit: yup.string().nullable(),
  status: yup.string().nullable()
});

const AddEditAccGroupTask = ({
  onClose,
  onSubmit,
  isSubmitting,
  modalType,
  selectedTask,
  setModalType,
  blockId,
  tasks,
  taskPositionType
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    context: { taskPositionType }, // 👈 this is important
    defaultValues: {
      task_ids: []
    }
  });
  const [subActivityInput, setSubActivityInput] = useState('');
  const [subActivities, setSubActivities] = useState([]);
  const projectId = watch('project');
  const [allUsers, setAllUsers] = useState([{ id: null, lable: null, level: null }]);
  const [materialCategories, setMaterialCategories] = useState([
    {
      id: Date.now(),
      items: [{ id: Date.now(), item_id: '', quantity: '' }]
    }
  ]);
  const [plannedStartDate, setPlannedStartDate] = useState(null);

  console.log('foundddd', selectedTask);

  const UserID = Cookies.get('userId');

  // Define selectedProjectId early to avoid reference errors
  const selectedProjectId = selectedTask?.project_id;

  // Function to fetch items by project and category
  const getItemsByProjectAndCategory = useCallback(async (projectId, categoryId) => {
    if (!projectId || !categoryId) return [];
    try {
      return await getAllItemsByProjectAndCategoryID(projectId, categoryId);
    } catch (error) {
      console.error('Error fetching items:', error);
      return [];
    }
  }, []);

  const { data: AllCategory, refetch: refetchCategories } = useQuery({
    queryKey: ['getCategory', UserID],
    queryFn: () => {
      if (!UserID) {
        return Promise.resolve([]); // protect against undefined values
      }
      return getAllBuilderCategories(UserID);
    },
    enabled: !!UserID
  });

  const { data: AllUnits, refetch: refetchUnits } = useQuery({
    queryKey: ['blocks', blockId],
    queryFn: () => getPlcbyBlockId(blockId),
    enabled: !!blockId
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Add Edit Acc Group Live Task Viewed', {
      user_id: UserID,
      timestamp: new Date().toISOString()
    });
  }, [UserID]);

  useEffect(() => {
    refetchUnits();
  }, [blockId]);

  const { data: AllBlocks, refetch: refetchBlocks } = useQuery({
    queryKey: ['getAllBlocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId
  });

  useEffect(() => {
    refetchCategories();
  }, []);

  const { data: AllWorkTypes, refetch: fetchWorkTypes } = useQuery({
    queryKey: ['AllWorkTypes'],
    queryFn: getAllWorkTypes
  });

  const { data: AllUsers, refetch: refetchUsers } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsers(),
    keepPreviousData: true
  });

  useEffect(() => {
    setAllUsers([]); // Clear old users first

    if (!AllUsers) return;

    const usersArray = AllUsers?.data || AllUsers;

    const filteredUsers = usersArray
      .filter((user) => {
        return user.vertical === 'Operations';
      })
      .map((user) => ({
        user: {
          id: user.id,
          name: user.name,
          level: user.level
        }
      }));

    setAllUsers(filteredUsers);
  }, [AllUsers]);

  const { data: AllContractors, refetch: refetchContractors } = useQuery({
    queryKey: ['getContractorAndArchitect1'],
    queryFn: () => getContractorAndArchitect(1)
    // keepPreviousData: true
  });

  const { data: AllArchitect, refetch: refetchArchitects } = useQuery({
    queryKey: ['getContractorAndArchitect2'],
    queryFn: () => getContractorAndArchitect(2)
    // keepPreviousData: true
  });

  useEffect(() => {
    refetchArchitects();
    refetchContractors();
    fetchWorkTypes();
    refetchUsers();
  }, []);

  const transformedDataWorkTypes =
    AllWorkTypes?.filter((item) => item.type === 'Accomodation') // ✅ filter first
      .map((item) => ({
        label: item.name || '',
        id: item.id,
        value: item.id
      })) || [];

  const transformedEngineer = allUsers.map((item) => ({
    name: `${item.user?.name?.trim() || ''}${item.user?.level ? ` (${item.user.level === 'Medium' ? 'Manager' : 'Executive'})` : ''}`,
    id: item.user?.id,
    level: item.user?.level
  }));

  const transformedConractor = AllContractors?.data?.map((item) => ({
    name: `${item.user?.name || ''}${
      item.serviceProviderWorkTypeMapping?.length > 0
        ? ` (${item.serviceProviderWorkTypeMapping
            .map((item) => item?.workType?.name)
            .filter((name) => name)
            .join(', ')})`
        : ''
    }`,
    id: item.user?.id
  }));

  const transformedArchitect = AllArchitect?.data?.map((item) => ({
    name: item.user?.name?.trim() || '',
    id: item.user?.id
  }));

  const transformedDataUnits =
    AllUnits?.filter((item) => item.accommodation?.accomodationType === 'GROUP')
      .map((item) => ({
        label: item.unitName || '',
        id: item.id,
        value: item.id
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  // Pre-fill form in edit mode
  useEffect(() => {
    // Reset fields and state when switching modes or clearing selection
    if (modalType !== 'Edit' || !selectedTask) {
      reset(); // Reset react-hook-form fields
      setSubActivities([]); // Reset sub-activities
      setMaterialCategories([
        // Reset material categories
        { id: Date.now(), items: [{ id: Date.now(), item_id: '', quantity: '' }] }
      ]);
      setPlannedStartDate(null); // Reset planned start date
      return; // Exit early if not in Edit mode or no task selected
    }

    // Ensure we have the necessary data structures before proceeding
    if (selectedTask && tasks.length > 0) {
      console.log('selectedTask', selectedTask);
      // Pre-fill basic fields
      setValue('idleTime', selectedTask.delay || '');
      setValue('task_name', selectedTask.name || '');
      setValue('duration', selectedTask.duration || '');
      setValue('labour_budget', selectedTask.labour_budget || '');
      setValue('material_budget', selectedTask.material_budget || '');
      setSubActivities(selectedTask.subActivity || []);
      setValue('drawingRequired', selectedTask.drawing_required ? 'Yes' : 'No');
      setValue('workType', selectedTask.workType_id || '');
      setValue('unit', selectedTask.unit?.id || '');
      setValue('status', selectedTask.status || '');
      // Pre-fill planned start date if available
      if (selectedTask.planned_start_date) {
        const formattedDate = new Date(selectedTask.planned_start_date).toISOString().split('T')[0];
        setPlannedStartDate(formattedDate);
      } else {
        setPlannedStartDate(null);
      }

      // Pre-fill Dependency Tasks (using the nested dependency object)
      const preSelectedDependencies = (selectedTask.dependencies || []) // Use selectedTask.dependencies
        .map((depMapping) => ({
          id: depMapping.dependency?.id,
          name: depMapping.dependency?.name?.trim() || ''
        }))
        ?.filter((dep) => dep.id && dep.name); // Filter out invalid entries

      // Deduplicate before setting
      const uniqueDependencies = Array.from(
        new Map(preSelectedDependencies.map((dep) => [dep.id, dep])).values() // Use ID for uniqueness
      );
      setValue('task_ids', uniqueDependencies);

      // Pre-fill Engineers
      const preSelectedEngineers = (selectedTask.engineerLiveTaskMapping || [])
        .map((mapping) => ({
          id: mapping.user?.id,
          name: `${mapping.user?.name?.trim() || ''}${mapping.user?.level ? ` (${mapping.user.level === 'Medium' ? 'Manager' : 'Executive'})` : ''}`,
          level: mapping.user?.level
        }))
        ?.filter((eng) => eng.id && eng.name); // Filter out invalid entries
      setValue('engineer_ids', preSelectedEngineers);

      // Pre-fill Contractors (category_id 1)
      const preSelectedContractors = (selectedTask.spLiveTaskMapping || [])
        ?.filter((mapping) => mapping.user?.serviceProvider?.category_id === 1)
        .map((mapping) => ({
          id: mapping.user?.id,
          name: mapping.user?.name?.trim() || ''
        }))
        ?.filter((sp) => sp.id && sp.name); // Filter out invalid entries
      setValue('sp_ids', preSelectedContractors);

      // Pre-fill Architects (category_id 2)
      const preSelectedArchitects = (selectedTask.architectLiveTaskMapping || [])
        ?.filter((mapping) => mapping.user?.serviceProvider?.category_id === 2)
        .map((mapping) => ({
          id: mapping.user?.id,
          name: mapping.user?.name?.trim() || ''
        }))
        ?.filter((arc) => arc.id && arc.name); // Filter out invalid entries
      setValue('architect_ids', preSelectedArchitects);

      // Pre-fill Materials
      if (selectedTask.materialTaskMapping && selectedTask.materialTaskMapping.length > 0) {
        // Group materials by category
        const groupedMaterials = selectedTask.materialTaskMapping.reduce((acc, material) => {
          // Get category_id from the nested item object
          const categoryId = material.item?.category?.id || material.item?.category_id;
          if (categoryId) {
            if (!acc[categoryId]) {
              acc[categoryId] = [];
            }
            // Ensure quantity is a string for the input field if needed, or handle type conversion
            const quantity = material.quantity != null ? String(material.quantity) : '';
            acc[categoryId].push({
              id: Date.now() + Math.random(), // Temporary ID for the item row
              item_id: String(material.item_id),
              item_name: material.item?.item_name || material.item?.name,
              unit: material.unit || '',
              quantity: quantity,
              category_id: categoryId // Store category_id for easier mapping in render
            });
          }
          return acc;
        }, {});

        // For each category, fetch available items and ensure all edited items are present
        const fetchAndSetCategories = async () => {
          const entries = await Promise.all(
            Object.entries(groupedMaterials).map(async ([categoryId, items]) => {
              let available_items = [];
              if (selectedProjectId && categoryId) {
                try {
                  const apiItems = await getAllItemsByProjectAndCategoryID(
                    selectedProjectId,
                    categoryId
                  );
                  available_items = (apiItems || []).map((apiItem) => ({
                    id: String(apiItem.id),
                    name: apiItem.item?.name,
                    unit: apiItem.unit || apiItem.item?.units?.[0] || apiItem.item?.uom || 'unit'
                  }));
                  // Ensure all items being edited are present in available_items
                  items.forEach((item) => {
                    if (!available_items.some((ai) => ai.id === item.item_id)) {
                      available_items.push({
                        id: item.item_id,
                        name: item.item_name,
                        unit: item.unit
                      });
                    }
                  });
                } catch (error) {
                  // fallback: just use the edited items
                  available_items = items.map((item) => ({
                    id: item.item_id,
                    name: item.item_name,
                    unit: item.unit
                  }));
                }
              } else {
                available_items = items.map((item) => ({
                  id: item.item_id,
                  name: item.item_name,
                  unit: item.unit
                }));
              }
              return {
                id: Date.now() + Math.random(),
                category_id: categoryId,
                available_items,
                items
              };
            })
          );
          setMaterialCategories(entries);
        };
        fetchAndSetCategories();
      } else {
        // Reset if there's no material data in selectedTask
        setMaterialCategories([
          { id: Date.now(), items: [{ id: Date.now(), item_id: '', quantity: '' }] }
        ]);
      }
    }
    // Ensure all dependencies used within the effect are listed. reset added. AllCategory added. setMaterialCategories added.
  }, [modalType, selectedTask, tasks, setValue, reset, AllCategory, setMaterialCategories]);

  const onSubmitForm = (formValues) => {
    const userId = Cookies.get('userId');
    // if (!formValues.task_ids || formValues.task_ids.length < 1) {
    //   toast.error('Please select dependency task');
    //   return;
    // }

    if (!formValues.engineer_ids || formValues.engineer_ids.length < 1) {
      toast.error('Please select engineer');
      return;
    }

    if (formValues.engineer_ids.filter((e) => e.level === 'Medium').length !== 1) {
      toast.error('Please select exactly one Manager');
      return;
    }

    // Contractor selection is optional
    // if (!formValues.sp_ids || formValues.sp_ids.length < 1) {
    //   toast.error('Please select serviceProvider');
    //   return;
    // }

    // if (!formValues.architect_ids || formValues.architect_ids.length < 1) {
    //   toast.error('Please select architect');
    //   return;
    // }

    const materials = [];

    for (const category of materialCategories) {
      for (const item of category.items) {
        const hasAnyValue = item.item_id || item.quantity;

        // If any value is provided, require all fields
        if (hasAnyValue) {
          if (!item.item_id || !item.quantity) {
            toast.error('If you select an item, quantity must be provided.');
            return;
          }

          // Get the unit from the selected item
          const selectedUnit = item.unit || 'unit';

          materials.push({
            item_id: Number(item.item_id),
            unit: selectedUnit,
            quantity: Number(item.quantity)
          });
        }
      }
    }

    let payload = {
      name: capitalizeWords(formValues.task_name),
      delay: Number(formValues.idleTime),
      duration: Number(formValues.duration),
      dependencies: formValues.task_ids?.map((item) => item.id),
      drawing_required: formValues.drawingRequired === 'Yes',
      subActivity: subActivities.map((activity) => capitalizeWords(activity)),
      workType_id: Number(formValues.workType),
      materials: materials || [],
      labour_budget: Number(formValues.labour_budget) || null,
      material_budget: Number(formValues.material_budget) || null,
      engineer_ids: (formValues.engineer_ids || []).map((e) => e.id),
      sp_ids: formValues.sp_ids?.map((sp) => sp.id),
      architect_ids: formValues.architect_ids?.map((arch) => arch.id),
      unit_id: Number(formValues.unit) || null,
      planned_start_date: plannedStartDate || null
    };

    if (modalType === 'Edit') {
      payload = { ...payload, status: formValues.status };
    }
    onSubmit(payload);
  };

  const handleIdleNumberChange = useCallback(
    (val) => {
      let cleaned = val.replace(/[^0-9]/g, '');
      setValue('idleTime', cleaned);
    },
    [setValue]
  );

  const handleLBNumberChange = useCallback(
    (val) => {
      let cleaned = val.replace(/[^0-9]/g, '');
      setValue('labour_budget', cleaned);
    },
    [setValue]
  );

  const handleMBNumberChange = useCallback(
    (val) => {
      let cleaned = val.replace(/[^0-9]/g, '');
      setValue('material_budget', cleaned);
    },
    [setValue]
  );

  const handleDurationNumberChange = useCallback(
    (val) => {
      let cleaned = val.replace(/[^0-9]/g, '');
      setValue('duration', cleaned);
    },
    [setValue]
  );

  const transformedDataCompany = tasks?.map((item) => ({
    name: item.name?.trim(),
    id: item.id
  }));

  const handleAddSubActivity = () => {
    if (subActivityInput.trim() !== '') {
      setSubActivities((prev) => [...prev, capitalizeWords(subActivityInput.trim())]);
      setSubActivityInput('');
    }
  };

  const handleRemoveSubActivity = (index) => {
    setSubActivities((prev) => prev?.filter((_, i) => i !== index));
  };

  const handleAddCategory = () => {
    setMaterialCategories([
      ...materialCategories,
      {
        id: Date.now() + Math.random(),
        items: [{ id: Date.now() + Math.random(), item_id: '', quantity: '' }]
      }
    ]);
  };

  const handleAddItem = (categoryId) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: [
                ...category.items,
                { id: Date.now() + Math.random(), item_id: '', quantity: '' }
              ]
            }
          : category
      )
    );
  };

  const handleRemoveItem = (categoryId, itemId) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: category?.items?.filter((item) => item.id !== itemId)
            }
          : category
      )
    );
  };

  const handleRemoveCategory = (categoryId) => {
    setMaterialCategories((prev) => prev?.filter((category) => category.id !== categoryId));
  };

  const handleCategoryChange = async (categoryId, selectedCategoryId) => {
    // First update the category immediately for UI feedback
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              category_id: selectedCategoryId,
              available_items: [], // Clear items initially
              items: category.items.map((item) => ({
                ...item,
                category_id: selectedCategoryId,
                item_id: '', // Reset item when category changes
                quantity: ''
              }))
            }
          : category
      )
    );

    // Fetch items for the selected category and project
    let categoryItems = [];
    if (selectedProjectId && selectedCategoryId) {
      try {
        categoryItems = await getAllItemsByProjectAndCategoryID(
          selectedProjectId,
          selectedCategoryId
        );

        // Map new API response to available_items for dropdown
        const mappedItems = (categoryItems || []).map((apiItem) => ({
          id: apiItem.id, // OUTER id (parent id)
          name: apiItem.item?.name,
          unit: apiItem.unit || apiItem.item?.units?.[0] || apiItem.item?.uom || 'unit'
        }));

        // Update with fetched items
        setMaterialCategories((prev) =>
          prev.map((category) =>
            category.id === categoryId
              ? {
                  ...category,
                  available_items: mappedItems
                }
              : category
          )
        );
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to fetch items for selected category'
        );
      }
    } else {
      if (!selectedProjectId) {
        toast.warning('Project ID not available from selectedTask');
      }
    }
  };

  const handleItemChange = (categoryId, itemId, selectedItemInfo) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.map((item) =>
                item.id === itemId
                  ? {
                      ...item,
                      item_id: selectedItemInfo.id,
                      item_name: selectedItemInfo.name,
                      unit: selectedItemInfo.unit
                    }
                  : item
              )
            }
          : category
      )
    );
  };

  const handleQuantityChange = (categoryId, itemId, quantity) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.map((item) =>
                item.id === itemId
                  ? {
                      ...item,
                      quantity: quantity
                    }
                  : item
              )
            }
          : category
      )
    );
  };

  return (
    <>
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Task Details</span>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6 relative">
        <div className="flex justify-start items-center w-full flex-wrap gap-4">
          <FormInput
            width={23.69}
            label="WorkType"
            register={register}
            register_key="workType"
            type="dropdown"
            options={[
              { label: 'Select WorkType', value: '', selected: true, disabled: true },
              ...transformedDataWorkTypes
            ]}
            errors={errors.type}
            readOnly={modalType === 'Edit'}
            isRequired
          />

          <FormInput
            width={23.69}
            label="Task Name"
            register={register}
            register_key="task_name"
            type="text"
            isRequired
            errors={errors.task_name}
            readOnly={modalType === 'Edit'}
          />

          {modalType === 'Edit' && (
            <FormInput
              width={23.69}
              label="Status"
              register={register}
              register_key="status"
              type="dropdown"
              options={[
                { label: 'Select Status', value: '', selected: true, disabled: true },
                { label: 'To Do', value: 'To Do' },
                { label: 'In Progress', value: 'In Progress' },
                { label: 'Review', value: 'Review' },
                { label: 'Completed', value: 'Completed' }
              ]}
              errors={errors.status}
              isRequired
            />
          )}
          {selectedTask &&
            !['Structural Work', 'Brick Work', 'Plaster Work'].includes(
              selectedTask.template?.workType?.name
            ) && (
              <FormInput
                width={23.69}
                label={'Unit'}
                register={register}
                register_key={'unit'}
                isRequired={false}
                type={'dropdown'}
                options={[
                  { label: 'Select Unit', value: '', disabled: true, selected: true },
                  ...transformedDataUnits
                ]}
                validation={{ required: 'Unit is required' }}
                errors={errors.unit}
              />
            )}

          <FormInput
            width={23.69}
            label="Duration (in days)"
            register={register}
            register_key="duration"
            type="text"
            isRequired
            onType={handleDurationNumberChange}
            errors={errors.duration}
          />
          <div className="w-[23.69%]">
            <label className="text-gray-600 mb-1 block">
              {' '}
              Dependency Task {taskPositionType && taskPositionType === 'Other' ? '*' : ''}
            </label>
            <Controller
              name="task_ids"
              control={control}
              render={({ field }) => (
                <Multiselect
                  options={transformedDataCompany || []}
                  selectedValues={field.value}
                  onSelect={(selectedList) => {
                    console.log('Selected Task:', selectedList);
                    field.onChange(selectedList);
                  }}
                  onRemove={(selectedList) => {
                    console.log('Updated task:', selectedList);
                    field.onChange(selectedList);
                  }}
                  displayValue="name"
                  placeholder=""
                  showCheckbox
                  isRequired={taskPositionType && taskPositionType === 'Other'}
                />
              )}
            />
            {errors.task_ids && <span className="text-red-500">{errors.task_ids.message}</span>}
          </div>
          <FormInput
            width={23.69}
            register={register}
            register_key="idleTime"
            type="idleTimeInput"
            onType={handleIdleNumberChange}
            isRequired
            errors={errors.idleTime}
          />

          {/* Planned Start Date - Only show when task status is "To Do" */}
          {selectedTask?.status === 'To Do' && (
            <div className="w-[23.69%]">
              <div className="flex flex-col gap-2 text-gray-700">
                <div className="flex justify-between items-start space-x-2 w-fit">
                  <span className="text-gray-600">Planned Start Date</span>
                </div>
                <input
                  type="date"
                  value={plannedStartDate || ''}
                  onChange={(e) => setPlannedStartDate(e.target.value)}
                  className="w-full p-2 border-[1px] border-stone-300 rounded"
                />
              </div>
            </div>
          )}

          <div className="w-[23.69%]">
            <label className="text-gray-600 mb-1 block">Assign Employee *</label>
            <Controller
              name="engineer_ids"
              control={control}
              render={({ field }) => (
                <Multiselect
                  key={JSON.stringify(field.value)}
                  options={transformedEngineer.filter((eng) => {
                    const hasMedium = field.value?.some((sel) => sel.level === 'Medium');
                    const hasLow = field.value?.some((sel) => sel.level === 'Low');

                    // hide all Medium if one Medium is already selected (except the selected one)
                    if (
                      hasMedium &&
                      eng.level === 'Medium' &&
                      !field.value?.some((sel) => sel.id === eng.id)
                    ) {
                      return false;
                    }

                    // hide all Low if one Low is already selected (except the selected one)
                    if (
                      hasLow &&
                      eng.level === 'Low' &&
                      !field.value?.some((sel) => sel.id === eng.id)
                    ) {
                      return false;
                    }

                    return true;
                  })}
                  selectedValues={field.value}
                  onSelect={(selectedList) => {
                    console.log('Selected Task:', selectedList);
                    field.onChange(selectedList);
                  }}
                  onRemove={(selectedList) => {
                    console.log('Updated task:', selectedList);
                    field.onChange(selectedList);
                  }}
                  displayValue="name"
                  placeholder=""
                  showCheckbox
                  isRequired
                />
              )}
            />
            {errors.engineer_ids && (
              <span className="text-red-500">{errors.engineer_ids.message}</span>
            )}
          </div>

          <div className="w-[23.69%]">
            <label className="text-gray-600 mb-1 block">Assign Contractor</label>
            <Controller
              name="sp_ids"
              control={control}
              render={({ field }) => (
                <Multiselect
                  options={transformedConractor || []}
                  selectedValues={field.value}
                  onSelect={(selectedList) => {
                    console.log('Selected Task:', selectedList);
                    field.onChange(selectedList);
                  }}
                  onRemove={(selectedList) => {
                    console.log('Updated task:', selectedList);
                    field.onChange(selectedList);
                  }}
                  displayValue="name"
                  placeholder=""
                  showCheckbox
                  // isRequired
                />
              )}
            />
            {errors.sp_ids && <span className="text-red-500">{errors.sp_ids.message}</span>}
          </div>
          <div className="w-[23.69%]">
            <label className="text-gray-600 mb-1 block">Assign Architect</label>
            <Controller
              name="architect_ids"
              control={control}
              render={({ field }) => (
                <Multiselect
                  options={transformedArchitect || []}
                  selectedValues={field.value}
                  onSelect={(selectedList) => {
                    console.log('Selected Task:', selectedList);
                    field.onChange(selectedList);
                  }}
                  onRemove={(selectedList) => {
                    console.log('Updated task:', selectedList);
                    field.onChange(selectedList);
                  }}
                  displayValue="name"
                  placeholder=""
                  showCheckbox
                  // isRequired={watch('drawingRequired') === 'Yes'}
                />
              )}
            />
            {errors.architect_ids && (
              <span className="text-red-500">{errors.architect_ids.message}</span>
            )}
          </div>

          <FormInput
            width={23.69}
            label="Labour Budget"
            register={register}
            register_key="labour_budget"
            type="text"
            onType={handleLBNumberChange}
            isRequired={false}
            errors={errors.labour_budget}
          />

          <FormInput
            width={23.69}
            label="Material Budget"
            register={register}
            register_key="material_budget"
            type="text"
            onType={handleMBNumberChange}
            isRequired={false}
            errors={errors.material_budget}
          />

          <div className="flex justify-between flex-col items-start w-[23.69%] gap-2 ">
            <label className="text-gray-600" htmlFor="drawing">
              Drawing Required *
            </label>
            <div className="flex justify-start items-center w-[100%] gap-2 space-x-4">
              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  name="drawingRequired"
                  value="Yes"
                  {...register('drawingRequired')}
                />
                <span>Yes</span>
              </label>

              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  name="drawingRequired"
                  value="No"
                  {...register('drawingRequired')}
                />
                <span>No</span>
              </label>
            </div>
            {errors.drawingRequired && (
              <span className="text-red-500">{errors.drawingRequired.message}</span>
            )}
          </div>

          <div className="flex flex-col w-[23.69%]">
            <label className="text-gray-600">Subactivity </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={subActivityInput}
                onChange={(e) => setSubActivityInput(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 w-full"
              />
              <Button
                customStyle={{ padding: '0.3rem 1rem' }}
                type="button"
                onClick={handleAddSubActivity}>
                +
              </Button>
            </div>
            {/* Display subactivities */}
            {subActivities.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {subActivities.map((sub, index) => (
                  <div key={index} className="flex items-center bg-gray-200 rounded px-2 py-1">
                    <span>{sub}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSubActivity(index)}
                      className="ml-2 text-red-500">
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="w-full flex justify-end items-center space-x-4 mt-8">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit">{modalType === 'Add' ? 'Submit' : 'Update'}</Button>
        </div>
      </form>
    </>
  );
};

export default AddEditAccGroupTask;
